import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { Badge, Card, Skeleton } from 'antd';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import moment from 'moment';
import { Dayjs } from 'dayjs';
import { LeedflowDailyStats } from '../../../hooks/calls/useCallsStats';

import styles from './ActivityChart.module.css';

interface ActivityChartProps {
  icon: React.ReactNode;
  label: string;
  iconBgColor: 'green' | 'blue';
  dailyStats: LeedflowDailyStats[];
  status: ContextStatus;
  period?: [Dayjs | null, Dayjs | null];
  comingSoon?: boolean;
}

const ActivityChart: React.FC<ActivityChartProps> = ({ icon, label, iconBgColor, dailyStats, status, period, comingSoon }) => {
  const categories = dailyStats.map(stat => moment(stat.timestamp).format('ddd D MMMM'));
  const smsSent = dailyStats.map(stat => stat.smsSentCount);
  const smsReceived = dailyStats.map(stat => stat.smsReceivedCount);
  const callsOutbound = dailyStats.map(stat => stat.callsOutboundCount);
  const callsInbound = dailyStats.map(stat => stat.callsInboundCount);
  const totals = dailyStats.map(
    stat =>
      (stat.smsSentCount ?? 0) +
      (stat.smsReceivedCount ?? 0) +
      (stat.callsOutboundCount ?? 0) +
      (stat.callsInboundCount ?? 0)
  );
  // Trend line (linear regression): a straight line that indicates the overall direction (upward / downward)
  const trend = (() => {
    const startIdx = totals.findIndex(v => v > 0);
    if (startIdx === -1) return totals.map(() => null);

    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = startIdx; i < totals.length; i += 1) {
      xs.push(i);
      ys.push(totals[i] ?? 0);
    }

    const n = xs.length;
    const sumX = xs.reduce((acc, v) => acc + v, 0);
    const sumY = ys.reduce((acc, v) => acc + v, 0);
    const sumXY = xs.reduce((acc, x, idx) => acc + x * ys[idx], 0);
    const sumX2 = xs.reduce((acc, x) => acc + x * x, 0);

    const denom = n * sumX2 - sumX * sumX;
    if (denom === 0) {
      const flat = Math.round(ys[0] ?? 0);
      return totals.map((_, i) => (i < startIdx ? null : flat));
    }

    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;

    return totals.map((_, i) => {
      if (i < startIdx) return null;
      const y = slope * i + intercept;
      return Math.max(0, Math.round(y));
    });
  })();

  // Determine x-axis title based on whether a custom period is selected
  const xAxisTitle = period && period[0] && period[1] ? 'Période sélectionnée' : '60 derniers jours';

  const chartOptions = {
    chart: {
      // Mixed chart: stacked bars + a line to highlight the overall trend
      type: 'line' as const,
      height: 210,
      stacked: true,
      toolbar: {
        show: true,
      },
    },
    xaxis: {
      categories,
      title: {
        text: xAxisTitle,
      },
    },
    yaxis: {
      title: {
        text: 'Nb de messages/appels',
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 5,
        borderRadiusWhenStacked: 'last' as const,
      },
    },
    dataLabels: {
      // Too noisy for this kind of chart (60 days). Keep tooltips to read values instead of labels.
      enabled: false,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => `${val}`,
      },
    },
    stroke: {
      width: [0, 0, 0, 0, 3],
      curve: 'straight' as const,
      dashArray: [0, 0, 0, 0, 6],
    },
    markers: {
      size: [0, 0, 0, 0, 0],
    },
    colors: [
      '#FFEC38', // SMS Received
      '#ffad66', // SMS Sent
      '#6ce077', // Inbound Calls
      '#169c33', // Outbound Calls
      '#1f6feb', // Trend line
    ],
    legend: {
      position: 'top' as const,
    },
    fill: {
      opacity: 1,
    },
  };

  const chartSeries = [
    {
      name: 'SMS Reçus',
      data: smsReceived,
      type: 'bar' as const,
    },
    {
      name: 'SMS Envoyés',
      data: smsSent,
      type: 'bar' as const,
    },
    {
      name: 'Appels Entrants',
      data: callsInbound,
      type: 'bar' as const,
    },
    {
      name: 'Appels Sortants',
      data: callsOutbound,
      type: 'bar' as const,
    },
    {
      name: 'Tendance (droite)',
      data: trend,
      type: 'line' as const,
    },
  ];

  return (
      <Card className={styles.statCard}>
        <div className={styles.statCardHeader}>
          <div className={styles.statCardIcon}>{icon}</div>
          <span className={styles.statCardLabel}>{label}</span>
          {comingSoon && <Badge count="Soon" className={styles.soonBadge} />}
        </div>
        {status === ContextStatus.LOADING ?
          <Skeleton active /> :
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={210}
          />}
      </Card>
  );
};

export default ActivityChart;
